import clsx from 'clsx';
import { ChangeEvent, useState } from 'react';
import { Chip, TextField, Zoom } from '@material-ui/core';
import { OpenWith, Send } from '@material-ui/icons';
import { CcModal } from '../CcModal';
import { ExpandResponseModal } from './ExpandResponseModal';
import { AttachFileButton } from './AttachFileButton';
import { TransitionGroup } from 'react-transition-group';
import { useSupportTicket } from '../../../hooks/SupportTicket/useSupportTicket';
import { SupportTicket } from '../../../types/commonTypes';
import { CcButton } from '../CcButton';
import { xsScreen } from '../../../lib/constants';
import { formatBytes } from '../../../lib/formatters';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { Tooltip } from '../../../components/Tooltip';
import { IconButton } from '../../../components/IconButton';
import { Button } from '../../../components/Button/NewButton';

interface Props {
  ticketInfo: SupportTicket;
  setUpdateSubmitted(bool: boolean): void;
}

export const TicketResponseBar = (props: Props) => {
  const { ticketInfo, setUpdateSubmitted } = props;

  const ccList = ticketInfo
    ? ticketInfo.updates[ticketInfo.updates.length - 1].message.ccList !== ''
      ? ticketInfo.updates[ticketInfo.updates.length - 1].message.ccList.split(
          ', '
        )
      : []
    : undefined;

  const [ccModalOpen, setCcModalOpen] = useState(false);
  const [expandResponse, setExpandResponse] = useState(false);
  const [contactList, setContactList] = useState<string[] | undefined>(ccList);
  const [response, setResponse] = useState('');
  const [attachments, setAttachments] = useState<any>([]);
  const isMobile = useMediaQuery(xsScreen);
  const { loading, addMessage } = useSupportTicket();

  const resetFields = () => {
    setResponse('');
    setAttachments([]);
  };

  const updateTicketMessage = () => {
    addMessage(
      ticketInfo.id,
      response,
      contactList !== undefined ? contactList.toString() : '',
      attachments[0]
    );
    resetFields();

    setUpdateSubmitted(true);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setResponse(event.target.value);
  };

  const handleDelete = (fileName: string) => {
    setAttachments(
      attachments.filter((attachment: any) => attachment.filename !== fileName)
    );
  };

  return (
    <>
      <div>
        {attachments && attachments.length > 0 ? (
          <TransitionGroup className="flex flex-wrap items-center justify-end min-h-[35px] p-1 my-2">
            {attachments.map((attachment: any, index: number) => {
              return (
                <Zoom key={`attachment-${index}`}>
                  <Tooltip
                    title={formatBytes(attachment.size, 2)}
                    showArrow
                    placement="top"
                  >
                    <Chip
                      className="m-1"
                      key={`upload-${index + 1}`}
                      label={attachment.filename}
                      onDelete={() => handleDelete(attachment.filename)}
                    />
                  </Tooltip>
                </Zoom>
              );
            })}
          </TransitionGroup>
        ) : null}
      </div>
      <div
        className={clsx('inline-flex items-center flex-wrap w-full p-4', {
          '': isMobile,
        })}
      >
        <CcButton
          badgeContent={contactList?.length}
          onClick={() => setCcModalOpen(true)}
        />
        <div className="flex items-center flex-grow rounded-full border border-border-light dark:border-border-dark px-4">
          <TextField
            className="w-full"
            InputProps={{ disableUnderline: true }}
            multiline
            maxRows={2}
            placeholder="Write a message..."
            value={response}
            onChange={handleChange}
          />
          <Tooltip title="Expand response" showArrow placement="top-start">
            <IconButton onClick={() => setExpandResponse(true)}>
              <OpenWith />
            </IconButton>
          </Tooltip>
        </div>
        {!isMobile ? (
          <AttachFileButton
            attachments={attachments}
            setAttachments={setAttachments}
            limitFileCount={1}
            limitFileSize={12000000}
            limitBuffer={20000}
          />
        ) : null}
        <Button
          className="!w-full sm:!w-fit mt-2 sm:mt-0"
          theme="action"
          startIcon={<Send />}
          disabled={response.trim().length == 0}
          onClick={updateTicketMessage}
        >
          SUBMIT
        </Button>
      </div>
      <CcModal
        ccList={contactList ? contactList : ['']}
        onClose={() => setCcModalOpen(false)}
        open={ccModalOpen}
        updateCcList={setContactList}
        portalOut={document.getElementById('ticket-modal') as Element}
        maxWidth="md"
      />

      <ExpandResponseModal
        response={response}
        updateResponse={setResponse}
        open={expandResponse}
        onClose={() => setExpandResponse(false)}
      />
    </>
  );
};
